import React, { useEffect, useRef, useState } from "react";
import { sendTextMessage } from "../../actions/chatActions";
import { useChatUpload } from "../../hooks/chat/useChatUpload";
import { wsManager } from "../../websocket/wsManager";
import { useDebounceCallback } from "../../hooks/useDebounceCallback";
import {
  CHAT_IMAGE_ACCEPT,
  CHAT_UNSUPPORTED_IMAGE_MESSAGE,
  isAcceptedChatImageFile,
} from "../../shared/imageFileContract";
import ChatImageEditorModal from "./ChatImageEditorModal";
import {
  CHAT_ATTACHMENT_QUEUE_LIMIT,
  CHAT_ATTACHMENT_QUEUE_LIMIT_MESSAGE,
  createFileAttachmentItem,
  fileExtensionOf,
  formatAttachmentSize,
  isFileAttachment,
  isImageAttachment,
  remainingAttachmentCapacity,
  takeWithinCapacity,
  hasNonSendableQueuedAttachment,
} from "./chatAttachmentQueue";
import {
  MEDIA_KIND_MESSAGE,
  QUEUE_PHASE,
  canRetryPrepare,
  canRetryUpload,
  hasBlockingPrepare,
  hasUploadableQueueItems,
  isItemUploadReady,
  queuePhaseLabel,
} from "../../shared/propertyMediaQueue/propertyMediaQueue";
import usePropertyMediaQueue from "../../shared/propertyMediaQueue/usePropertyMediaQueue";
import EntitySharePicker from "./EntitySharePicker";

/** Same breakpoint as `.chat-composer` phone CSS in modernize.css. */
export const CHAT_COMPOSER_PHONE_MEDIA = "(max-width: 767.98px)";

/** Aliases — mixed attachment cap replaced the image-only cap. */
export const CHAT_IMAGE_QUEUE_LIMIT = CHAT_ATTACHMENT_QUEUE_LIMIT;
export const CHAT_IMAGE_QUEUE_LIMIT_MESSAGE =
  CHAT_ATTACHMENT_QUEUE_LIMIT_MESSAGE;

const FILE_ICON_BY_EXT = {
  xlsx: "/images/xlsx.png",
  accdb: "/images/accdb.png",
  docx: "/images/docx.png",
  pptx: "/images/pptx.png",
  pub: "/images/pub.png",
  pdf: "/images/pdf.png",
};

function isTextEntryClusterTarget(node) {
  if (!node || typeof node.closest !== "function") return false;
  if (node.id === "chat-compose-text" || node.id === "chat-send-btn") return true;
  return Boolean(node.closest('[data-testid="chat-composer-send"]'));
}

function readIsPhoneComposerLayout() {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  try {
    return Boolean(window.matchMedia(CHAT_COMPOSER_PHONE_MEDIA).matches);
  } catch {
    return false;
  }
}

function resolveUploadErrorMessage(error) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail.trim();
  if (Array.isArray(detail) && detail.length) return String(detail[0]);
  return "Не вдалося надіслати вкладення.";
}

function fileIconSrc(name) {
  return FILE_ICON_BY_EXT[fileExtensionOf(name)] || null;
}

export default function MessageInput({
  chatId,
  isGroup,
  messagingBlocked = false,
  blockedByMe = false,
}) {
  const [text, setText] = useState("");
  const [showEntityPicker, setShowEntityPicker] = useState(false);
  const [textEntryActive, setTextEntryActive] = useState(false);
  const [isPhoneLayout, setIsPhoneLayout] = useState(readIsPhoneComposerLayout);
  const [queueItems, setQueueItems] = useState([]);
  const [queueError, setQueueError] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [composerSending, setComposerSending] = useState(false);
  const photoInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const editTriggerRef = useRef(null);
  const textEntryExitFrameRef = useRef(null);
  const composerSendingRef = useRef(false);
  const sendGenerationRef = useRef(0);
  const mountedRef = useRef(true);
  const messagingBlockedRef = useRef(messagingBlocked);
  const chatIdRef = useRef(chatId);
  const prevChatIdRef = useRef(chatId);
  const liveTextRef = useRef("");
  const { upload } = useChatUpload(chatId);

  chatIdRef.current = chatId;
  messagingBlockedRef.current = messagingBlocked;
  liveTextRef.current = text;

  const queueEnabled = !messagingBlocked;
  const mediaQueue = usePropertyMediaQueue({
    items: queueItems,
    setItems: setQueueItems,
    enabled: queueEnabled,
  });
  const { clearQueue, allocateLocalIds } = mediaQueue;

  const invalidateActiveSend = () => {
    sendGenerationRef.current += 1;
  };

  const cancelPendingTextEntryExit = () => {
    if (textEntryExitFrameRef.current != null) {
      window.cancelAnimationFrame(textEntryExitFrameRef.current);
      textEntryExitFrameRef.current = null;
    }
  };

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      invalidateActiveSend();
      cancelPendingTextEntryExit();
    };
  }, []);

  useEffect(() => {
    if (prevChatIdRef.current !== chatId) {
      invalidateActiveSend();
      clearQueue();
      setEditingItem(null);
      setQueueError(null);
      composerSendingRef.current = false;
      setComposerSending(false);
      prevChatIdRef.current = chatId;
    }
  }, [chatId, clearQueue]);

  useEffect(() => {
    if (messagingBlocked) {
      invalidateActiveSend();
      clearQueue();
      setEditingItem(null);
      setQueueError(null);
      composerSendingRef.current = false;
      setComposerSending(false);
    }
  }, [messagingBlocked, clearQueue]);

  useEffect(() => {
    if (!editingItem) return;
    const stillPresent = queueItems.some(
      (item) => item.localId === editingItem.localId,
    );
    if (!stillPresent) {
      setEditingItem(null);
    }
  }, [queueItems, editingItem]);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }

    let media;
    try {
      media = window.matchMedia(CHAT_COMPOSER_PHONE_MEDIA);
    } catch {
      return undefined;
    }

    const sync = () => setIsPhoneLayout(Boolean(media.matches));
    sync();

    if (typeof media.addEventListener === "function") {
      media.addEventListener("change", sync);
      return () => media.removeEventListener("change", sync);
    }
    if (typeof media.addListener === "function") {
      media.addListener(sync);
      return () => media.removeListener(sync);
    }
    return undefined;
  }, []);

  const activateTextEntry = () => {
    cancelPendingTextEntryExit();
    setTextEntryActive(true);
  };

  const scheduleTextEntryExit = (relatedTarget) => {
    if (isTextEntryClusterTarget(relatedTarget)) return;
    cancelPendingTextEntryExit();
    // Defer: pointer transfer to send may blur textarea with a null
    // relatedTarget before focus lands on the send control.
    textEntryExitFrameRef.current = window.requestAnimationFrame(() => {
      textEntryExitFrameRef.current = null;
      if (isTextEntryClusterTarget(document.activeElement)) {
        setTextEntryActive(true);
        return;
      }
      setTextEntryActive(false);
    });
  };

  const resetFileInputs = () => {
    if (photoInputRef.current) photoInputRef.current.value = "";
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const openPhotoPicker = () => {
    if (composerSendingRef.current) return;
    if (queueItems.length >= CHAT_ATTACHMENT_QUEUE_LIMIT) return;
    photoInputRef.current?.click();
  };

  const openFilePicker = () => {
    if (composerSendingRef.current) return;
    if (queueItems.length >= CHAT_ATTACHMENT_QUEUE_LIMIT) return;
    fileInputRef.current?.click();
  };

  const handlePhotoSelection = (fileList) => {
    const selected = Array.from(fileList || []);
    resetFileInputs();
    if (!selected.length || messagingBlocked || composerSendingRef.current) {
      return;
    }

    const remaining = remainingAttachmentCapacity(queueItems.length);
    if (remaining <= 0) {
      setQueueError(CHAT_ATTACHMENT_QUEUE_LIMIT_MESSAGE);
      return;
    }

    const accepted = [];
    let rejected = false;
    selected.forEach((file) => {
      if (isAcceptedChatImageFile(file)) {
        accepted.push(file);
      } else {
        rejected = true;
      }
    });

    const toAdd = takeWithinCapacity(accepted, remaining);
    const overLimit = accepted.length > remaining;

    if (overLimit) {
      setQueueError(CHAT_ATTACHMENT_QUEUE_LIMIT_MESSAGE);
    } else if (rejected) {
      setQueueError(CHAT_UNSUPPORTED_IMAGE_MESSAGE);
    } else {
      setQueueError(null);
    }

    if (toAdd.length) {
      mediaQueue.addFiles(toAdd, MEDIA_KIND_MESSAGE, { chatId });
    }
  };

  const handleFileSelection = (fileList) => {
    const selected = Array.from(fileList || []);
    resetFileInputs();
    if (!selected.length || messagingBlocked || composerSendingRef.current) {
      return;
    }

    const remaining = remainingAttachmentCapacity(queueItems.length);
    if (remaining <= 0) {
      setQueueError(CHAT_ATTACHMENT_QUEUE_LIMIT_MESSAGE);
      return;
    }

    const toAdd = takeWithinCapacity(selected, remaining);
    if (selected.length > remaining) {
      setQueueError(CHAT_ATTACHMENT_QUEUE_LIMIT_MESSAGE);
    } else {
      setQueueError(null);
    }

    if (!toAdd.length) return;
    const ids = allocateLocalIds(toAdd.length);
    const added = toAdd.map((file, idx) =>
      createFileAttachmentItem(file, ids[idx], { chatId }),
    );
    setQueueItems((prev) => [...prev, ...added]);
  };

  const removeAttachment = (localId) => {
    if (composerSendingRef.current) return;
    invalidateActiveSend();
    if (editingItem?.localId === localId) {
      setEditingItem(null);
    }
    mediaQueue.removeItem(localId);
  };

  const submitComposer = async () => {
    if (composerSendingRef.current || messagingBlockedRef.current || !chatId) {
      return;
    }
    if (editingItem) return;
    if (hasBlockingPrepare(queueItems)) return;
    if (hasNonSendableQueuedAttachment(queueItems, chatId)) return;

    const boundChatId = chatId;
    const uploadForBoundChat = upload;
    const snapshotText = liveTextRef.current.trim();
    const readySnapshot = [];
    queueItems.forEach((it) => {
      if (!isItemUploadReady(it) && it.phase !== QUEUE_PHASE.upload_failed) {
        return;
      }
      if (it.phase === QUEUE_PHASE.upload_failed && it.uploadFile) {
        mediaQueue.retryUpload(it.localId);
      }
      readySnapshot.push({
        localId: it.localId,
        uploadFile: it.uploadFile,
        chatId: it.chatId != null ? it.chatId : boundChatId,
        kind: it.kind,
      });
    });
    if (readySnapshot.length !== queueItems.length) return;
    if (!snapshotText && !readySnapshot.length) return;

    const generation = ++sendGenerationRef.current;
    composerSendingRef.current = true;
    setComposerSending(true);
    setQueueError(null);

    const isActiveSendOwner = () =>
      mountedRef.current
      && !messagingBlockedRef.current
      && chatIdRef.current === boundChatId
      && sendGenerationRef.current === generation;

    let attachmentsFailed = false;

    try {
      for (const entry of readySnapshot) {
        if (!isActiveSendOwner()) {
          return;
        }
        if (entry.chatId !== boundChatId) {
          return;
        }

        mediaQueue.patchItem(entry.localId, {
          phase: QUEUE_PHASE.uploading,
          uploadError: null,
        });

        try {
          if (isFileAttachment(entry)) {
            await uploadForBoundChat([entry.uploadFile], [], {
              onComplete: resetFileInputs,
            });
          } else {
            await uploadForBoundChat([], [entry.uploadFile], {
              onComplete: resetFileInputs,
            });
          }
          if (!isActiveSendOwner()) {
            return;
          }
          mediaQueue.applyUploadOutcome({ succeededIds: [entry.localId] });
        } catch (error) {
          if (!isActiveSendOwner()) {
            return;
          }
          mediaQueue.applyUploadOutcome({
            failures: [{
              localId: entry.localId,
              error: resolveUploadErrorMessage(error),
            }],
          });
          attachmentsFailed = true;
          break;
        }
      }

      if (attachmentsFailed || !isActiveSendOwner()) {
        return;
      }
      if (snapshotText) {
        sendTextMessage(boundChatId, snapshotText);
        liveTextRef.current = "";
        if (mountedRef.current) {
          setText("");
        }
      }
    } finally {
      if (sendGenerationRef.current === generation) {
        composerSendingRef.current = false;
        if (mountedRef.current) {
          setComposerSending(false);
        }
        resetFileInputs();
      }
    }
  };

  const handleTyping = (e) => {
    if (messagingBlocked) return;
    setText(e.target.value);

    sendTyping();
  };

  const sendTyping = useDebounceCallback(() => {
    if (messagingBlocked) return;
    wsManager.send({
      event: "typing",
      chat_id: chatId,
    });
  }, 500);

  const handleKeyDown = (e) => {
    if (e.key !== "Enter") return;

    if (e.shiftKey) {
      return;
    }

    e.preventDefault();
    submitComposer();
  };

  if (messagingBlocked) {
    const blockedCopy = blockedByMe
      ? "Ви заблокували цього користувача. Розблокуйте його, щоб продовжити спілкування."
      : "Обмін повідомленнями з цим користувачем недоступний.";
    return (
      <div
        className="chat-footer chat-composer-blocked"
        data-testid="chat-composer-blocked"
        role="status"
      >
        <span className="chat-composer-blocked__icon" aria-hidden="true">
          <i className="fa-solid fa-user-slash" />
        </span>
        <span className="chat-composer-blocked__text">{blockedCopy}</span>
      </div>
    );
  }

  // Compact UX is phone-only; textEntryActive still tracks focus so
  // breakpoint transitions while the cluster stays focused stay coherent.
  const compactModeActive = isPhoneLayout && textEntryActive;
  const composerClassName = compactModeActive
    ? "chat-footer chat-composer chat-composer--text-focus"
    : "chat-footer chat-composer";
  const attachmentTabIndex = compactModeActive ? -1 : undefined;
  const queueBusy =
    composerSending
    || queueItems.some((it) => it.phase === QUEUE_PHASE.uploading)
    || hasBlockingPrepare(queueItems)
    || Boolean(editingItem);
  const queueHasNonSendable = hasNonSendableQueuedAttachment(
    queueItems,
    chatId,
  );
  const hasSendablePayload =
    Boolean(text.trim()) || hasUploadableQueueItems(queueItems);
  const canSubmit =
    hasSendablePayload && !queueBusy && !queueHasNonSendable;
  const queueMutationLocked = composerSending;
  const attachmentCount = queueItems.length;
  const imageCount = queueItems.filter(isImageAttachment).length;
  const fileCount = queueItems.filter(isFileAttachment).length;
  const queueAtCapacity = attachmentCount >= CHAT_ATTACHMENT_QUEUE_LIMIT;
  const addControlsDisabled = queueAtCapacity || composerSending;

  return (
    <div
      className="chat-composer-shell"
      data-testid="chat-composer-shell"
    >
      {queueItems.length > 0 ? (
        <div
          className="chat-composer__attachment-tray chat-composer__image-queue"
          data-testid="chat-image-queue"
        >
          <div className="chat-composer__image-queue-header">
            <span
              className="chat-composer__image-queue-count"
              data-testid="chat-image-queue-count"
            >
              {attachmentCount}/{CHAT_ATTACHMENT_QUEUE_LIMIT} вкладень
            </span>
          </div>
          <ul
            className="chat-composer__image-queue-list"
            aria-label="Черга вкладень"
          >
            {queueItems.map((item, idx) => {
              const fileRow = isFileAttachment(item);
              const showEdit =
                !fileRow
                && isItemUploadReady(item)
                && Boolean(item.uploadFile)
                && item.phase !== QUEUE_PHASE.uploading;
              const showPrepareRetry = !fileRow && canRetryPrepare(item);
              const showUploadRetry = canRetryUpload(item);
              const displayName =
                (item.edited && item.uploadFile?.name)
                || item.uploadFile?.name
                || item.file?.name
                || (fileRow ? `Файл ${idx + 1}` : `Зображення ${idx + 1}`);
              const sizeLabel = formatAttachmentSize(
                item.uploadFile?.size ?? item.file?.size,
              );
              const statusText =
                item.prepareError
                || item.uploadError
                || (sizeLabel
                  ? `${queuePhaseLabel(item.phase)} · ${sizeLabel}`
                  : queuePhaseLabel(item.phase));
              const iconSrc = fileRow ? fileIconSrc(displayName) : null;
              return (
                <li
                  key={item.localId}
                  className={
                    fileRow
                      ? "chat-composer__image-queue-item chat-composer__image-queue-item--file"
                      : "chat-composer__image-queue-item"
                  }
                  data-testid={`chat-image-queue-item-${item.localId}`}
                  data-phase={item.phase}
                  data-kind={fileRow ? "file" : "image"}
                >
                  <div className="chat-composer__image-queue-thumb">
                    {!fileRow && item.previewUrl ? (
                      <img
                        src={item.previewUrl}
                        alt=""
                        className="chat-composer__image-queue-preview"
                      />
                    ) : (
                      <span
                        className="chat-composer__image-queue-placeholder"
                        aria-hidden="true"
                      >
                        {iconSrc ? (
                          <img src={iconSrc} alt="" />
                        ) : (
                          <i
                            className={
                              fileRow
                                ? "fa-regular fa-file"
                                : "fa-regular fa-image"
                            }
                          />
                        )}
                      </span>
                    )}
                  </div>
                  <div className="chat-composer__image-queue-meta">
                    <span className="chat-composer__image-queue-name">
                      {displayName}
                    </span>
                    <span
                      className="chat-composer__image-queue-status"
                      data-testid={`chat-image-queue-status-${item.localId}`}
                    >
                      {statusText}
                    </span>
                  </div>
                  <div className="chat-composer__image-queue-actions">
                    {showEdit ? (
                      <button
                        type="button"
                        className="chat-composer__image-queue-action"
                        data-testid={`chat-image-queue-edit-${item.localId}`}
                        disabled={queueBusy || queueMutationLocked}
                        onClick={(event) => {
                          if (composerSendingRef.current) return;
                          editTriggerRef.current = event.currentTarget;
                          setEditingItem({
                            localId: item.localId,
                            uploadFile: item.uploadFile,
                          });
                        }}
                        aria-label={`Редагувати зображення ${idx + 1}`}
                      >
                        <i className="fa-solid fa-pen" aria-hidden="true" />
                        <span className="chat-composer__image-queue-action-label">
                          Редагувати
                        </span>
                      </button>
                    ) : null}
                    {showPrepareRetry ? (
                      <button
                        type="button"
                        className="chat-composer__image-queue-action"
                        disabled={queueBusy || queueMutationLocked}
                        onClick={() => {
                          if (composerSendingRef.current) return;
                          mediaQueue.retryPrepare(item.localId);
                        }}
                        aria-label={`Повторити підготовку ${idx + 1}`}
                      >
                        <i className="fa-solid fa-rotate-right" aria-hidden="true" />
                        <span className="chat-composer__image-queue-action-label">
                          Повторити
                        </span>
                      </button>
                    ) : null}
                    {showUploadRetry ? (
                      <button
                        type="button"
                        className="chat-composer__image-queue-action"
                        disabled={queueBusy || queueMutationLocked}
                        onClick={() => {
                          if (composerSendingRef.current) return;
                          mediaQueue.retryUpload(item.localId);
                        }}
                        aria-label={
                          fileRow
                            ? `Повторити завантаження файлу ${idx + 1}`
                            : `Повторити завантаження ${idx + 1}`
                        }
                      >
                        <i className="fa-solid fa-rotate-right" aria-hidden="true" />
                        <span className="chat-composer__image-queue-action-label">
                          Повторити
                        </span>
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="chat-composer__image-queue-action chat-composer__image-queue-action--remove"
                      disabled={
                        queueMutationLocked
                        || item.phase === QUEUE_PHASE.uploading
                      }
                      onClick={() => removeAttachment(item.localId)}
                      aria-label={
                        fileRow
                          ? `Видалити файл ${idx + 1}`
                          : `Видалити зображення ${idx + 1}`
                      }
                    >
                      <i className="fa-solid fa-xmark" aria-hidden="true" />
                      <span className="chat-composer__image-queue-action-label">
                        Видалити
                      </span>
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
          {queueError ? (
            <p className="chat-composer__image-queue-error" role="alert">
              {queueError}
            </p>
          ) : null}
        </div>
      ) : null}

      <ChatImageEditorModal
        show={Boolean(editingItem?.uploadFile)}
        sourceFile={editingItem?.uploadFile || null}
        kind={MEDIA_KIND_MESSAGE}
        title="Редагувати зображення"
        restoreFocusRef={editTriggerRef}
        disabled={composerSending}
        onApply={(file) => {
          if (composerSendingRef.current || !editingItem) return;
          mediaQueue.applyEditedFile(editingItem.localId, file);
          setEditingItem(null);
        }}
        onCancel={() => setEditingItem(null)}
      />

      <div className={composerClassName}>
      <form action="" data-testid="chat-composer-form">
        <div className="send-section" data-testid="chat-composer-input-row">
          <div
            className="send-file"
            data-testid="chat-composer-attachments"
            aria-hidden={compactModeActive ? "true" : undefined}
            inert={compactModeActive ? true : undefined}
          >
            <button
              type="button"
              id="chat-send-photo-btn"
              className="chat-composer__control"
              aria-label="Надіслати фото"
              title="Надіслати фото"
              disabled={addControlsDisabled}
              tabIndex={attachmentTabIndex}
              onClick={openPhotoPicker}
            >
              <i className="fa-regular fa-image" aria-hidden="true"></i>
              <sup id="photo-count" className="send-file-count">
                {imageCount > 0 ? imageCount : ""}
              </sup>
            </button>
            <input
              className="form-control d-none"
              type="file"
              id="send-photo"
              ref={photoInputRef}
              accept={CHAT_IMAGE_ACCEPT}
              tabIndex={attachmentTabIndex}
              onChange={(e) => handlePhotoSelection(e.target.files)}
              multiple
            />
            <button
              type="button"
              id="chat-send-file-btn"
              className="chat-composer__control"
              aria-label="Надіслати файл"
              title="Надіслати файл"
              disabled={addControlsDisabled}
              tabIndex={attachmentTabIndex}
              onClick={openFilePicker}
            >
              <i className="fa-regular fa-file" aria-hidden="true"></i>
              <sup id="file-count" className="send-file-count">
                {fileCount > 0 ? fileCount : ""}
              </sup>
            </button>
            <input
              className="form-control d-none"
              type="file"
              id="send-file"
              ref={fileInputRef}
              accept=".accdb, .xlsx, .pptx, .docx, .pub, .pdf"
              tabIndex={attachmentTabIndex}
              onChange={(e) => handleFileSelection(e.target.files)}
              multiple
            />
            {!isGroup && (
              <button
                type="button"
                id="chat-entity-share-btn"
                className="chat-composer__control"
                aria-label="Надіслати картку"
                title="Надіслати картку"
                disabled={composerSending}
                tabIndex={attachmentTabIndex}
                onClick={() => {
                  if (composerSendingRef.current) return;
                  setShowEntityPicker(true);
                }}
              >
                <i className="fa-regular fa-address-card" aria-hidden="true"></i>
              </button>
            )}
          </div>
          <div className="send-text">
            <textarea
              className="form-control"
              id="chat-compose-text"
              placeholder="Повідомлення"
              aria-label="Повідомлення"
              value={text}
              disabled={composerSending}
              onChange={handleTyping}
              onKeyDown={handleKeyDown}
              onFocus={activateTextEntry}
              onBlur={(e) => scheduleTextEntryExit(e.relatedTarget)}
            ></textarea>
          </div>
          <div className="send-btn" data-testid="chat-composer-send">
            <button
              className={
                queueBusy
                  ? "chat-composer__control chat-composer__control--busy"
                  : "chat-composer__control"
              }
              type="button"
              id="chat-send-btn"
              value="Send"
              aria-label="Надіслати"
              title="Надіслати"
              disabled={!canSubmit}
              aria-busy={queueBusy ? "true" : "false"}
              onClick={submitComposer}
              onFocus={activateTextEntry}
              onBlur={(e) => scheduleTextEntryExit(e.relatedTarget)}
              onPointerDown={activateTextEntry}
            >
              <i className="fa-regular fa-paper-plane" aria-hidden="true"></i>
            </button>
          </div>
        </div>
      </form>
      </div>
      {!isGroup && (
        <EntitySharePicker
          chatId={chatId}
          show={showEntityPicker}
          onHide={() => setShowEntityPicker(false)}
        />
      )}
    </div>
  );
}
